import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { 
  AlertCircle, 
  CheckCircle2, 
  Clock, 
  MessageSquare, 
  Plus, 
  Search, 
  Filter,
  Send,
  Trash2,
  Edit,
  User,
  Calendar,
  Tag,
  Flag,
  XCircle,
  ArrowLeft,
  MoreVertical
} from "lucide-react";
import { formatDateDDMonYYYY } from "../utils/dateFormat";

const PRIORITY_CONFIG = {
  low: { label: "Low", color: "bg-gray-100 text-gray-700 border-gray-300" },
  medium: { label: "Medium", color: "bg-blue-100 text-blue-700 border-blue-300" },
  high: { label: "High", color: "bg-orange-100 text-orange-700 border-orange-300" },
  critical: { label: "Critical", color: "bg-red-100 text-red-700 border-red-300" }
};

const STATUS_CONFIG = {
  open: { label: "Open", color: "bg-yellow-100 text-yellow-700 border-yellow-300", icon: AlertCircle },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-700 border-blue-300", icon: Clock },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-700 border-green-300", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-700 border-gray-300", icon: XCircle }
};

const CATEGORY_CONFIG = {
  general: { label: "General", color: "bg-gray-100 text-gray-600" },
  bug: { label: "Bug", color: "bg-red-100 text-red-600" },
  feature: { label: "Feature Request", color: "bg-purple-100 text-purple-600" },
  question: { label: "Question", color: "bg-cyan-100 text-cyan-600" }
};

const IssueTracker = ({ user }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState({ isOpen: false, issue: null, comments: [] });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, issueId: null });
  
  // Form state
  const [newIssue, setNewIssue] = useState({
    title: "",
    description: "",
    priority: "medium",
    category: "general"
  });
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchIssues();
  }, [statusFilter, priorityFilter]);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (priorityFilter !== "all") params.append("priority", priorityFilter);
      
      const response = await axiosInstance.get(`/issues?${params.toString()}`);
      setIssues(response.data);
    } catch (error) {
      toast.error("Failed to fetch issues");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIssue = async () => {
    if (!newIssue.title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!newIssue.description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    setSubmitting(true);
    try {
      await axiosInstance.post("/issues", newIssue);
      toast.success("Issue created successfully!");
      setNewIssue({ title: "", description: "", priority: "medium", category: "general" });
      setCreateDialog(false);
      fetchIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create issue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewIssue = async (issue) => {
    try {
      const response = await axiosInstance.get(`/issues/${issue.id}`);
      setViewDialog({ isOpen: true, issue: response.data.issue, comments: response.data.comments });
    } catch (error) {
      toast.error("Failed to load issue details");
    }
  };

  const handleUpdateStatus = async (issueId, newStatus) => {
    try {
      await axiosInstance.put(`/issues/${issueId}`, { status: newStatus });
      toast.success(`Issue marked as ${STATUS_CONFIG[newStatus].label}`);
      fetchIssues();
      // Update view dialog if open
      if (viewDialog.isOpen && viewDialog.issue?.id === issueId) {
        const response = await axiosInstance.get(`/issues/${issueId}`);
        setViewDialog({ ...viewDialog, issue: response.data.issue });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update issue");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setSubmitting(true);
    try {
      await axiosInstance.post(`/issues/${viewDialog.issue.id}/comments`, { content: newComment });
      toast.success("Comment added!");
      setNewComment("");
      // Refresh issue details
      const response = await axiosInstance.get(`/issues/${viewDialog.issue.id}`);
      setViewDialog({ ...viewDialog, issue: response.data.issue, comments: response.data.comments });
      fetchIssues(); // Update comment count in list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axiosInstance.delete(`/issues/${viewDialog.issue.id}/comments/${commentId}`);
      toast.success("Comment deleted");
      // Refresh issue details
      const response = await axiosInstance.get(`/issues/${viewDialog.issue.id}`);
      setViewDialog({ ...viewDialog, issue: response.data.issue, comments: response.data.comments });
      fetchIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete comment");
    }
  };

  const handleDeleteIssue = async () => {
    try {
      await axiosInstance.delete(`/issues/${deleteDialog.issueId}`);
      toast.success("Issue deleted successfully");
      setDeleteDialog({ isOpen: false, issueId: null });
      setViewDialog({ isOpen: false, issue: null, comments: [] });
      fetchIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete issue");
    }
  };

  const filteredIssues = issues.filter(issue => 
    issue.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    issue.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    issue.created_by_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: issues.length,
    open: issues.filter(i => i.status === "open").length,
    inProgress: issues.filter(i => i.status === "in_progress").length,
    resolved: issues.filter(i => i.status === "resolved").length
  };

  const canModifyIssue = (issue) => {
    return issue.created_by === user?.id || user?.role === 'admin' || user?.role === 'superuser';
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Issue Tracker</h2>
          <p className="text-muted-foreground">Report and track issues, bugs, or feature requests</p>
        </div>
        <Button onClick={() => setCreateDialog(true)} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4" />
          Report Issue
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="shadow-lg border-l-4 border-l-gray-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Issues</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-lg border-l-4 border-l-yellow-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Open</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.open}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-lg border-l-4 border-l-blue-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-lg border-l-4 border-l-green-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Resolved</p>
                <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-lg">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search issues..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Issues List */}
      <div className="space-y-4">
        {loading ? (
          <Card className="shadow-lg">
            <CardContent className="py-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Loading issues...</p>
            </CardContent>
          </Card>
        ) : filteredIssues.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700">No issues found</p>
              <p className="text-gray-500 mb-4">
                {searchTerm || statusFilter !== "all" || priorityFilter !== "all" 
                  ? "Try adjusting your filters" 
                  : "Click 'Report Issue' to create your first issue"}
              </p>
              {!searchTerm && statusFilter === "all" && priorityFilter === "all" && (
                <Button onClick={() => setCreateDialog(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Report Issue
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredIssues.map((issue) => {
            const StatusIcon = STATUS_CONFIG[issue.status]?.icon || AlertCircle;
            return (
              <Card 
                key={issue.id} 
                className="shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => handleViewIssue(issue)}
              >
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`${STATUS_CONFIG[issue.status]?.color} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {STATUS_CONFIG[issue.status]?.label}
                        </Badge>
                        <Badge className={`${PRIORITY_CONFIG[issue.priority]?.color} border`}>
                          <Flag className="w-3 h-3 mr-1" />
                          {PRIORITY_CONFIG[issue.priority]?.label}
                        </Badge>
                        <Badge className={CATEGORY_CONFIG[issue.category]?.color}>
                          {CATEGORY_CONFIG[issue.category]?.label}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold mb-1">{issue.title}</h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-2">{issue.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {issue.created_by_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDateTime(issue.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {issue.comment_count} comments
                        </span>
                      </div>
                    </div>
                    {issue.status !== "resolved" && issue.status !== "closed" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-300 hover:bg-green-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateStatus(issue.id, "resolved");
                        }}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Resolve
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Issue Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-indigo-600" />
              Report New Issue
            </DialogTitle>
            <DialogDescription>
              Describe the issue you're experiencing. Be as detailed as possible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                placeholder="Brief summary of the issue"
                value={newIssue.title}
                onChange={(e) => setNewIssue({ ...newIssue, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                placeholder="Detailed description of the issue, steps to reproduce, expected behavior..."
                value={newIssue.description}
                onChange={(e) => setNewIssue({ ...newIssue, description: e.target.value })}
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={newIssue.priority} onValueChange={(v) => setNewIssue({ ...newIssue, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={newIssue.category} onValueChange={(v) => setNewIssue({ ...newIssue, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="feature">Feature Request</SelectItem>
                    <SelectItem value="question">Question</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateIssue} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
              {submitting ? "Creating..." : "Create Issue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Issue Dialog */}
      <Dialog open={viewDialog.isOpen} onOpenChange={(open) => !open && setViewDialog({ isOpen: false, issue: null, comments: [] })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          {viewDialog.issue && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${STATUS_CONFIG[viewDialog.issue.status]?.color} border`}>
                        {STATUS_CONFIG[viewDialog.issue.status]?.label}
                      </Badge>
                      <Badge className={`${PRIORITY_CONFIG[viewDialog.issue.priority]?.color} border`}>
                        {PRIORITY_CONFIG[viewDialog.issue.priority]?.label}
                      </Badge>
                      <Badge className={CATEGORY_CONFIG[viewDialog.issue.category]?.color}>
                        {CATEGORY_CONFIG[viewDialog.issue.category]?.label}
                      </Badge>
                    </div>
                    <DialogTitle className="text-xl">{viewDialog.issue.title}</DialogTitle>
                  </div>
                  {canModifyIssue(viewDialog.issue) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteDialog({ isOpen: true, issueId: viewDialog.issue.id })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <DialogDescription className="text-left">
                  <span className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {viewDialog.issue.created_by_name} ({viewDialog.issue.created_by_role})
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDateTime(viewDialog.issue.created_at)}
                    </span>
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto space-y-4 py-4">
                {/* Description */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="whitespace-pre-wrap">{viewDialog.issue.description}</p>
                </div>

                {/* Resolution Info */}
                {viewDialog.issue.resolved_at && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700">
                      <CheckCircle2 className="w-4 h-4 inline mr-1" />
                      Resolved by <strong>{viewDialog.issue.resolved_by_name}</strong> on {formatDateTime(viewDialog.issue.resolved_at)}
                    </p>
                  </div>
                )}

                {/* Status Actions */}
                <div className="flex gap-2 flex-wrap">
                  {viewDialog.issue.status !== "resolved" && (
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleUpdateStatus(viewDialog.issue.id, "resolved")}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Mark Resolved
                    </Button>
                  )}
                  {viewDialog.issue.status === "resolved" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUpdateStatus(viewDialog.issue.id, "open")}
                    >
                      <AlertCircle className="w-4 h-4 mr-1" />
                      Reopen
                    </Button>
                  )}
                  {viewDialog.issue.status === "open" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUpdateStatus(viewDialog.issue.id, "in_progress")}
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      Mark In Progress
                    </Button>
                  )}
                </div>

                {/* Comments Section */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Comments ({viewDialog.comments.length})
                  </h4>
                  
                  <div className="space-y-3 mb-4 max-h-[200px] overflow-y-auto">
                    {viewDialog.comments.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No comments yet. Be the first to comment!</p>
                    ) : (
                      viewDialog.comments.map((comment) => (
                        <div key={comment.id} className="p-3 bg-white border rounded-lg">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm">{comment.created_by_name}</span>
                                <Badge variant="outline" className="text-xs">{comment.created_by_role}</Badge>
                                <span className="text-xs text-gray-500">{formatDateTime(comment.created_at)}</span>
                              </div>
                              <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
                            </div>
                            {(comment.created_by === user?.id || user?.role === 'admin' || user?.role === 'superuser') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 h-6 w-6 p-0"
                                onClick={() => handleDeleteComment(comment.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Comment */}
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={2}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleAddComment} 
                      disabled={submitting || !newComment.trim()}
                      className="self-end"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.isOpen} onOpenChange={(open) => !open && setDeleteDialog({ isOpen: false, issueId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Issue</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this issue? This will also delete all comments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteIssue} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default IssueTracker;
